import React, { useContext, useState, useEffect } from "react";
import { auth, db } from "../../firebase/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from "firebase/firestore";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [appSettings, setAppSettings] = useState({ registrationEnabled: false });

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, initializeUser);

    const settingsDocRef = doc(db, "settings", "registration");
    const unsubscribeSettings = onSnapshot(settingsDocRef, (docSnap) => {
      if (docSnap.exists() && docSnap.data().enabled) {
        setAppSettings({ registrationEnabled: true });
      } else {
        setAppSettings({ registrationEnabled: false });
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeSettings();
    };
  }, []);

  async function initializeUser(user) {
    setLoading(true);
    if (user) {
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (userData.status === 'inactive') {
            setCurrentUser(null);
            setUserLoggedIn(false);
            auth.signOut();
        } else {
            setCurrentUser({ ...user, ...userData });
            setUserLoggedIn(true);
        }
      } else {
        // User just created in Firebase console, assign superadmin role
        const newUserProfile = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email,
          role: 'superadmin',
          status: 'active',
          createdAt: serverTimestamp(),
        };
        await setDoc(userDocRef, newUserProfile);
        setCurrentUser({ ...user, ...newUserProfile });
        setUserLoggedIn(true);
      }
    } else {
      setCurrentUser(null);
      setUserLoggedIn(false);
    }
    setLoading(false);
  }

  const value = {
    userLoggedIn,
    currentUser,
    loading,
    appSettings
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
